import { useState } from 'react';
import type { FeedbackView } from '@alfred/shared';
import { api, describeError } from './api.js';

/**
 * 👍 / 👎 and a correction on one of Alfred's answers. Clicking the active thumb again clears it.
 * This is the raw material for Alfred learning Anthony's preferences (alfred.md → Compounding).
 */
export function Feedback({ turnId, initial }: { turnId: string; initial: FeedbackView | null }) {
  const [rating, setRating] = useState<1 | -1 | null>(initial?.rating ?? null);
  const [correction, setCorrection] = useState(initial?.correction ?? '');
  const [saved, setSaved] = useState(initial?.correction ?? '');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rate = async (value: 1 | -1) => {
    const next = rating === value ? null : value;
    const previous = rating;
    setRating(next);
    try {
      await api.feedback(turnId, { rating: next });
      setError(null);
      // A thumbs-down invites a correction, but never forces one.
      if (next === -1 && !saved) setEditing(true);
    } catch (err) {
      setRating(previous);
      setError(describeError(err));
    }
  };

  const save = async () => {
    try {
      await api.feedback(turnId, { correction: correction.trim() || null });
      setSaved(correction.trim());
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <div className="feedback">
      <div className="feedback-row">
        <button
          className={`fb-btn ${rating === 1 ? 'fb-active' : ''}`}
          aria-pressed={rating === 1}
          aria-label="Good answer"
          onClick={() => void rate(1)}
        >
          👍
        </button>
        <button
          className={`fb-btn ${rating === -1 ? 'fb-active fb-down' : ''}`}
          aria-pressed={rating === -1}
          aria-label="Bad answer"
          onClick={() => void rate(-1)}
        >
          👎
        </button>
        <button className="fb-link" onClick={() => setEditing((e) => !e)}>
          {saved ? 'Edit correction' : 'Correct'}
        </button>
      </div>
      {saved && !editing && <p className="fb-saved">Correction: {saved}</p>}
      {editing && (
        <div className="fb-edit">
          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            placeholder="What should Alfred have said or done?"
            rows={2}
            maxLength={4000}
            autoFocus
          />
          <div className="fb-actions">
            <button className="btn btn-small btn-primary" onClick={() => void save()}>
              Save
            </button>
            <button
              className="btn btn-small"
              onClick={() => {
                setCorrection(saved);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}
